import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, LogOut, UserRound } from 'lucide-react';
import { ROLE_LABELS } from '@shared/permissions';
import { useAuth } from '@/lib/auth';
import { initials } from '@/lib/format';
import schoolLogo from '@/assets/school-logo-sm.png';
import { SidebarMenuButton } from './Sidebar';
import { ThemeToggleButton } from './ThemeToggleButton';

export interface BreadcrumbItem {
  label: string;
  /** When present the crumb is a link; the last crumb should omit it. */
  to?: string;
}

export interface HeaderProps {
  /** Ancestor crumbs only; the page itself carries the single visible title. */
  breadcrumbs?: BreadcrumbItem[];
  /** Optional extra actions before the user menu (e.g. quick-add buttons). */
  actions?: ReactNode;
  onOpenSidebar?: () => void;
}

function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
  if (items.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" className="mt-1 hidden sm:block">
      <ol className="flex flex-wrap items-center gap-1 text-xs text-ink-muted">
        {items.map((item, index) => (
          <li key={`${item.label}-${index}`} className="flex items-center gap-1">
            {index > 0 && <ChevronRight aria-hidden="true" className="h-3 w-3" />}
            {index === items.length - 1 ? (
              <span aria-current="page" className="font-medium text-ink">
                {item.label}
              </span>
            ) : item.to ? (
              <Link to={item.to} className="hover:text-steel hover:underline">
                {item.label}
              </Link>
            ) : (
              <span>{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** White application header: breadcrumb trail, user menu and sign-out. */
export function Header({ breadcrumbs = [], actions, onOpenSidebar }: HeaderProps) {
  const { user, school, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target)) setMenuOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const roleLabel = user ? ROLE_LABELS[user.role] : '';

  return (
    <header className="app-header no-print sticky top-0 z-20 border-b border-steel-light/70 bg-surface shadow-card">
      <div className="flex items-center gap-3 px-4 py-3 md:px-6 lg:px-8">
        {onOpenSidebar && <SidebarMenuButton onClick={onOpenSidebar} />}

        <img
          src={schoolLogo}
          alt=""
          aria-hidden="true"
          className="hidden h-9 w-9 shrink-0 rounded-full border border-steel-light bg-white p-0.5 sm:block"
        />
        <div className="min-w-0 flex-1">
          <Breadcrumbs items={breadcrumbs} />
        </div>

        {actions && <div className="hidden items-center gap-2 lg:flex">{actions}</div>}

        <ThemeToggleButton />

        <div className="relative shrink-0" ref={menuRef}>
          <button
            ref={buttonRef}
            type="button"
            aria-haspopup="true"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className="flex items-center gap-2 rounded-full border border-steel-light bg-surface px-2 py-1.5 text-left transition-colors hover:border-steel-soft hover:bg-steel-pale focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azure"
          >
            <span
              aria-hidden="true"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy text-xs font-bold text-white"
            >
              {initials(user?.full_name)}
            </span>
            <span className="hidden min-w-0 sm:block">
              <span className="block max-w-40 truncate text-sm font-semibold text-ink">{user?.full_name}</span>
              <span className="block max-w-40 truncate text-xs text-ink-muted">{roleLabel}</span>
            </span>
          </button>

          {menuOpen && (
            <div
              aria-label="Account menu"
              className="absolute right-0 z-40 mt-2 w-64 rounded-lg border border-steel-light bg-surface p-2 shadow-pop"
            >
              <div className="border-b border-steel-light px-3 pb-2 pt-1">
                <p className="truncate text-sm font-semibold text-ink">{user?.full_name}</p>
                <p className="truncate text-xs text-ink-muted">{user?.email}</p>
                <p className="mt-1 text-xs font-medium text-steel">{roleLabel}</p>
                {school && <p className="mt-0.5 truncate text-xs text-ink-muted">{school.name}</p>}
              </div>
              <Link
                to="/change-password"
                onClick={() => setMenuOpen(false)}
                className="mt-1 flex items-center gap-2 rounded-md px-3 py-2 text-sm text-ink hover:bg-steel-pale"
              >
                <UserRound aria-hidden="true" className="h-4 w-4 text-steel" />
                Change password
              </Link>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  void signOut();
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-state-danger hover:bg-state-danger/10"
              >
                <LogOut aria-hidden="true" className="h-4 w-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
